import { useCallback, useRef } from 'react';
import { delayRender, continueRender, cancelRender, useVideoConfig } from 'remotion';
import { LottieElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';

const LOTTIE_API_URL = 'https://gh2zfyavs7.execute-api.us-east-1.amazonaws.com/lottie-transition';
const loadedAnimations = new Map<string, any>();

export const useLottieLoaderAnimations = () => {
  // const [loadedAnimations, setLoadedAnimations] = useState<Record<string, any>>({});
  const loadingRequestsCacheRef = useRef(new Map<string, Promise<any>>());

  const { fps } = useVideoConfig();

  const createLottieKey = useCallback((element: LottieElement): string => {
    return `${element.lottie_name}-${element.hex_colors.join('-')}`;
  }, []);

  const fetchLottieAnimation = useCallback(async (element: LottieElement): Promise<any> => {
    // eslint-disable-next-line goldcast/require-cvent-auth-headers -- external Lottie API, not a Goldcast/Cvent endpoint
    const response = await fetch(LOTTIE_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_seconds: (element.toFrame - element.fromFrame) / fps,
        lottie_name: element.lottie_name,
        hex_colors: element.hex_colors
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to load lottie animation: ${element.lottie_name}`);
    }

    return response.json();
  }, []);

  const loadAnimation = useCallback(
    async (element: LottieElement) => {
      const key = createLottieKey(element);
      const loadingRequestsCache = loadingRequestsCacheRef.current;

      // Return existing request if already loading
      if (loadingRequestsCache.has(key)) {
        return loadingRequestsCache.get(key);
      }

      // Create delay render handle
      const handle = delayRender(`Loading Lottie animation: ${key}`);

      const loadingPromise = (async () => {
        try {
          const animationData = await fetchLottieAnimation(element);

          // Update React state (handles both caching and re-renders)
          // setLoadedAnimations(prev => ({ ...prev, [key]: animationData }));
          loadedAnimations.set(key, animationData);
          // Continue render now that animation is loaded
          continueRender(handle);

          return animationData;
        } catch (error) {
          console.error(`Error loading lottie animation: ${element.lottie_name}`, error);
          cancelRender(error instanceof Error ? error.message : String(error));
        } finally {
          loadingRequestsCache.delete(key);
        }
      })();

      // Cache the promise IMMEDIATELY before returning
      loadingRequestsCache.set(key, loadingPromise);

      return loadingPromise;
    },
    [createLottieKey, fetchLottieAnimation]
  );

  const getAnimationData = useCallback(
    (element: LottieElement) => {
      const key = createLottieKey(element);
      const existingData = loadedAnimations.get(key);

      if (!existingData) {
        loadAnimation(element);
        return null;
      }

      return existingData;
    },
    [loadedAnimations, createLottieKey, loadAnimation]
  );

  return {
    getAnimationData
  };
};
